import {
  TemplateProvider,
  useTemplateContext,
} from '@aibees/elephant/components/export-dialog/context/TemplateContext';
import { useCreateDefaultExportTemplate } from '@aibees/elephant/components/export-dialog/hooks/use-create-default-export-template';
import { FEATURE_FLAGS } from '@aibees/elephant/constants';
import { SaveRecordAction } from '@aibees/elephant/hooks';
import { useLabelDialogConfig } from '@aibees/elephant/hooks/labels/use-label-dialog-config';
import { FeatureFlags } from '@aibees/elephant/types/feature-flags';
import { LabelType } from '@aibees/elephant/types/label';
import { useCreateOrUpdatePresetMutation } from '@aibees/shared/api/preset/use-create-or-update-preset-mutation';
import {
  DialogActions,
  DialogTitle,
  Form,
  FormOnSubmit,
  Icon,
} from '@aibees/shared/components';
import { isFeatureEnabled } from '@aibees/shared/feature-flags';
import { useBoolean, useDebounceFn, useNotify } from '@aibees/shared/hooks';
import withContextProvider from '@aibees/shared/utils/context';
import { Box, Dialog, DialogContent, Fade, Typography } from '@mui/material';
import { memo, useCallback, useMemo, useRef } from 'react';
import { ActionBulkModalAiLookalike } from '../action-bulk-modal-ai-lookalike';
import { AssignOwner } from '../assign-owner';
import {
  CreateActionBulkDialog,
  CreateActionBulkDialogData,
} from '../create-action-bulk-dialog';
import { CsvExportTemplateSettingActionBulkModal } from '../csv-export-template-setting-action-bulk-modal';
import { IntegrationConnectDialog } from '../integration-connect-dialog';
import { ListLabelActionBulkModal } from '../list-label-action-bulk-modal';
import { ExportDialogEditName } from './components/export-dialog-edit-name';
import { ExportIntegrationName } from './components/export-integration-name';
import { ExportSelectCampaign } from './components/export-select-campaign';
import { ExportTemplateCreateDialog } from './components/export-template-create-dialog';
import { IntegrationExportSetting } from './components/integration-export-setting';
import { ProfileInsightExportDialog } from './components/profile-insight-export-dialog';
import { scheme } from './config';
import {
  IntegrationProvider,
  useIntegrationContext,
} from './context/integration-context';
import { useConvertDataToExportSetting } from './hooks/use-convert-data-to-export-setting';
import { useExportDialog } from './hooks/use-export-dialog';
import { useExportNameValidation } from './hooks/use-export-name-validation';
import { useExportTemplateDataType } from './hooks/use-export-template-data-type';
import * as SX from './sx';
import { ExportDialogProps, type ExportDialogProps as Props } from './types';
import { hasProfileInsightFields } from './utils/has-profile-insight-fields';

const stopPropagation = (e: React.MouseEvent<HTMLDivElement>) =>
  e.stopPropagation();
const DIALOG_PAPER_PROPS = { onMouseDown: stopPropagation };

const ExportDialogComponent = memo(
  ({
    className: _className,
    table: _table,
    element,
    formId = 'export-dialog',
  }: Props) => {
    useCreateDefaultExportTemplate();
    const notify = useNotify();

    // Custom hook for export dialog logic
    const {
      exportDialogState,
      ids: _ids,
      expandedAccordion,
      showAiLookalike: _showAiLookalike,
      workspace,
      headerTitle,
      defaultValues,
      exportName,
      grouping,
      handleClose,
      handleExportWithAdditionalData,
      handleCloseIntegrationConnectDialog,
      setExpandedAccordion,
      setPreviousState,
      setExportName,
      integrationConfig,
    } = useExportDialog();

    // Local state
    const createLabelDialogState = useBoolean(false);
    const createListDialogState = useBoolean(false);
    const {
      setTemplateName,
      setSelectedTemplate,
      selectedTemplate,
      selectedPreset,
    } = useTemplateContext();
    const { selectedCampaign, setSelectedIntegration, setSelectedCampaign } =
      useIntegrationContext();
    const exportSetting = useConvertDataToExportSetting();
    const exportTemplateDataType = useExportTemplateDataType();
    const loading = useBoolean();
    const completenessDialogState = useBoolean(false);
    const {
      inputTitle: listInputTitle,
      titleDialog: listTitleDialog,
      placeholder: listPlaceholder,
    } = useLabelDialogConfig({ isLabel: false });
    const {
      inputTitle: labelInputTitle,
      titleDialog: labelTitleDialog,
      placeholder: labelPlaceholder,
    } = useLabelDialogConfig({ isLabel: true });

    const debouncedResetExportName = useDebounceFn(() => {
      setExportName('');
    }, 1000);
    const { mutateAsync } = useCreateOrUpdatePresetMutation<string>(
      'SELECTED_'.concat(exportTemplateDataType),
      false
    );
    const isProfileInsightExportDialogEnabled = isFeatureEnabled(
      FEATURE_FLAGS,
      FeatureFlags.PROFILE_INSIGHT_EXPORT_DIALOG
    );
    // Memoized values

    const { isValid: isExportNameValid } = useExportNameValidation(exportName);

    const isSubmitDisabled = useMemo(() => {
      return integrationConfig ? !selectedCampaign : !isExportNameValid;
    }, [integrationConfig, selectedCampaign, isExportNameValid]);

    // Handlers
    const handleCreate = useCallback(
      (data: CreateActionBulkDialogData) => {
        setTemplateName(data.name);
      },
      [setTemplateName]
    );

    const pendingFormDataRef = useRef<any>(null);

    const executeExport = useCallback(
      (data: any) => {
        loading.onTrue();
        if (!exportSetting) {
          notify.error('Please select a template to export.');
          return;
        }
        handleExportWithAdditionalData(
          data,
          {
            startProgress: loading.onTrue,
            endProgress: loading.onFalse,
            toggle: () => {
              handleClose('export');
              setSelectedCampaign(undefined);
              loading.onFalse();
            },
          },
          exportSetting
        );
        if (selectedTemplate && selectedTemplate.id !== selectedPreset?.value) {
          mutateAsync(
            selectedTemplate.id,
            'SELECTED_'.concat(exportTemplateDataType),
            selectedTemplate.name
          );
        }
      },
      [
        exportSetting,
        loading,
        notify,
        handleExportWithAdditionalData,
        handleClose,
        setSelectedCampaign,
        selectedTemplate,
        selectedPreset?.value,
        mutateAsync,
        exportTemplateDataType,
      ]
    );

    const handleSubmit: FormOnSubmit = useCallback(
      (data) => {
        if (!isProfileInsightExportDialogEnabled) {
          executeExport(data);
        }
        const isProfileInsightChecked =
          data[SaveRecordAction.CONTACT_REVEAL_PROFILE];

        if (
          !isProfileInsightChecked &&
          hasProfileInsightFields(exportSetting)
        ) {
          pendingFormDataRef.current = { ...data };
          completenessDialogState.onTrue();
          return;
        }

        executeExport(data);
      },
      [
        exportSetting,
        completenessDialogState,
        executeExport,
        isProfileInsightExportDialogEnabled,
      ]
    );

    const handleExportWithoutProfileInsight = useCallback(() => {
      completenessDialogState.onFalse();
      const pending = pendingFormDataRef.current;
      if (pending) {
        executeExport({
          ...pending,
          bypassProfileInsightChecking: true,
        });
        pendingFormDataRef.current = null;
      }
    }, [completenessDialogState, executeExport]);

    const handleApplyProfileInsightAndExport = useCallback(() => {
      completenessDialogState.onFalse();
      const pending = pendingFormDataRef.current;
      if (pending) {
        executeExport({
          ...pending,
          [SaveRecordAction.CONTACT_REVEAL_PROFILE]: true,
          bypassProfileInsightChecking: false,
        });
        pendingFormDataRef.current = null;
      }
    }, [completenessDialogState, executeExport]);

    const handleCloseDialog = useCallback(() => {
      handleClose('export');
      setPreviousState('');
      setSelectedIntegration(undefined);
      setSelectedCampaign(undefined);
      setSelectedTemplate(null);

      debouncedResetExportName();
      pendingFormDataRef.current = null;
    }, [
      handleClose,
      setPreviousState,
      setSelectedIntegration,
      setSelectedTemplate,
      debouncedResetExportName,
    ]);

    return (
      <>
        {element}

        <Form
          key={exportDialogState.value ? 'open' : 'closed'}
          id={formId}
          scheme={scheme}
          defaultValues={defaultValues}
          loading={loading.value}
          customSubmitButton
          sx={SX.form}
          onSubmit={handleSubmit}
        >
          <Dialog
            open={exportDialogState.value}
            fullWidth={true}
            maxWidth="xs"
            onClose={handleCloseDialog}
            TransitionComponent={Fade}
            PaperProps={DIALOG_PAPER_PROPS}
          >
            <DialogTitle
              title={headerTitle}
              icon="export"
              avatar={integrationConfig?.icon}
              onClose={handleCloseDialog}
            />

            {loading.value ? (
              <Box sx={SX.loadingWrapper}>
                <Icon name="loading-twotone-loop" size={24} />

                <Typography variant="body3" color="grey.800" fontWeight={600}>
                  Exporting...
                </Typography>
              </Box>
            ) : (
              <DialogContent padding="none">
                {integrationConfig ? (
                  <ExportIntegrationName
                    expandedAccordion={expandedAccordion}
                    setExpandedAccordion={setExpandedAccordion}
                  />
                ) : (
                  <ExportDialogEditName
                    defaultName={defaultValues.name}
                    expandedAccordion={expandedAccordion}
                    setExpandedAccordion={setExpandedAccordion}
                  />
                )}
                {integrationConfig && (
                  <ExportSelectCampaign
                    expandedAccordion={expandedAccordion}
                    setExpandedAccordion={setExpandedAccordion}
                  />
                )}
                <ListLabelActionBulkModal
                  type={LabelType.LIST}
                  state={createListDialogState}
                  firstState={exportDialogState}
                  optional={true}
                  expandedAccordion={expandedAccordion}
                  setExpandedAccordion={setExpandedAccordion}
                />
                <ListLabelActionBulkModal
                  type={LabelType.LABEL}
                  state={createLabelDialogState}
                  firstState={exportDialogState}
                  optional={true}
                  expandedAccordion={expandedAccordion}
                  setExpandedAccordion={setExpandedAccordion}
                />
                {workspace && (
                  <AssignOwner
                    expandedAccordion={expandedAccordion}
                    setExpandedAccordion={setExpandedAccordion}
                  />
                )}
                <ActionBulkModalAiLookalike
                  expandedAccordion={expandedAccordion}
                  setExpandedAccordion={setExpandedAccordion}
                />
                <CsvExportTemplateSettingActionBulkModal
                  expandedAccordion={expandedAccordion}
                  setExpandedAccordion={setExpandedAccordion}
                  firstState={exportDialogState}
                />
                {integrationConfig && (
                  <IntegrationExportSetting
                    expandedAccordion={expandedAccordion}
                    setExpandedAccordion={setExpandedAccordion}
                  />
                )}
              </DialogContent>
            )}

            <DialogActions
              formId={formId}
              submitTitle="Export"
              disabled={isSubmitDisabled || !exportSetting}
              onClose={handleCloseDialog}
            />
          </Dialog>

          <CreateActionBulkDialog
            state={createListDialogState}
            firstState={exportDialogState}
            title={listTitleDialog}
            placeholder={listPlaceholder}
            type={LabelType.LIST}
            inputTitle={listInputTitle}
            grouping={grouping}
          />

          <CreateActionBulkDialog
            state={createLabelDialogState}
            firstState={exportDialogState}
            title={labelTitleDialog}
            type={LabelType.LABEL}
            inputTitle={labelInputTitle}
            placeholder={labelPlaceholder}
            hasColor={true}
            hasDescription={true}
            onClick={handleCreate}
            grouping={grouping}
          />

          <ExportTemplateCreateDialog />

          <IntegrationConnectDialog
            onClose={handleCloseIntegrationConnectDialog}
          />

          {isProfileInsightExportDialogEnabled && (
            <ProfileInsightExportDialog
              open={completenessDialogState.value}
              onDismiss={() => {
                completenessDialogState.onFalse();
                pendingFormDataRef.current = null;
              }}
              onExportWithoutProfileInsight={handleExportWithoutProfileInsight}
              onApplyAndExport={handleApplyProfileInsightAndExport}
            />
          )}
        </Form>
      </>
    );
  }
);

ExportDialogComponent.displayName = 'ExportDialog';

export const ExportDialog = withContextProvider<ExportDialogProps>(
  ({ children }) => (
    <TemplateProvider>
      <IntegrationProvider>{children}</IntegrationProvider>
    </TemplateProvider>
  )
)(ExportDialogComponent);
